import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import CalendarModal from './CalendarModal';
import PlatformUpdatesPanel from './PlatformUpdatesPanel';
import { useTheme } from '../contexts/ThemeContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAdmin } from '../contexts/AdminContext';
import { useTranslation } from 'react-i18next';
import {
  Youtube,
  CheckCircle2,
  ChevronRight,
  PlayCircle,
  CalendarCheck,
  AlertTriangle,
  Calendar,
  Clapperboard,
  Mic,
  Flame,
  Target,
  TrendingUp,
  Inbox,
  Users,
  Eye,
  Video,
  Link2,
  ExternalLink,
  Monitor,
  Apple,
  Puzzle,
  Download,
  ArrowRight,
  Plus,
  Tv2,
  Lock,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { FaDiscord, FaWhatsapp } from 'react-icons/fa';
import { calculateProgress, getProgressColor, formatDate, isVideoOverdue } from './kanban/kanbanUtils';
import { useWindowsReleaseInfo } from '../hooks/useWindowsReleaseInfo';
import usePlatformSettings from '../hooks/usePlatformSettings';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace('.0', '') + 'K';
  return num.toString();
};

// Alias for backward compat
const formatViews = formatNumber;

// ===== LAZY THUMBNAIL — Intersection Observer lazy load =====
const LazyThumbnail = ({ video, isPrivate }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Prioridade: thumbnail_url (custom upload) > youtube_thumbnail (importados)
  const thumbnailUrl = video.thumbnail_url || video.youtube_thumbnail || null;

  const showImage = isVisible && thumbnailUrl && !isPrivate && !error;

  return (
    <div ref={ref} style={{
      width: '80px', height: '45px', borderRadius: '6px',
      background: 'var(--dp-bg-input)', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--dp-text-tertiary)', fontSize: '18px',
      overflow: 'hidden', position: 'relative',
    }}>
      {showImage && (
        <img
          src={thumbnailUrl}
          alt=""
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease',
          }}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      )}
      {(!showImage || !loaded) && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', height: '100%',
          position: showImage ? 'absolute' : 'static', inset: 0,
        }}>
          {isVisible && thumbnailUrl && !error ? (
            <div style={{
              width: '100%', height: '100%',
              background: 'linear-gradient(90deg, var(--dp-bg-input) 25%, var(--dp-bg-elevated) 50%, var(--dp-bg-input) 75%)',
              backgroundSize: '200% 100%',
              animation: 'dp-shimmer 1.5s infinite',
            }} />
          ) : (
            <PlayCircle size={18} />
          )}
        </div>
      )}
    </div>
  );
};

const DashboardNew = ({ onLogout }) => {
  usePageTitle('Dashboard');
  const { theme } = useTheme();
  const { isPrivate } = usePrivacy();
  const { sidebarWidth, isMobile } = useSidebar();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Comunidade WhatsApp é só para assinantes: premium = estado efetivo PREMIUM (ou super admin).
  const { subscriptionData } = useAdmin();
  const isPremium = !!(subscriptionData && (subscriptionData.is_super_admin || subscriptionData.estado_efetivo === 'PREMIUM'));
  // Links da comunidade: vêm do backend (editáveis pelo super admin no card)
  const { settings: platformSettings, saveSetting } = usePlatformSettings();
  const [editingLink, setEditingLink] = useState(null); // { key, value } | null
  const [savingLink, setSavingLink] = useState(false);

  const saveEditLink = async () => {
    if (!editingLink || savingLink) return;
    const value = (editingLink.value || '').trim();
    if (!value.startsWith('https://')) return;
    setSavingLink(true);
    try {
      await saveSetting(editingLink.key, value);
      toast.success(t('dashboard.community.link_saved'));
      setEditingLink(null);
    } catch {
      toast.error(t('dashboard.community.link_error'));
    } finally {
      setSavingLink(false);
    }
  };

  // Editor inline do link (só aparece pra super admin, no lugar do subtítulo)
  const renderLinkEditor = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }} onClick={(e) => e.stopPropagation()}>
      <input
        type="url"
        value={editingLink.value}
        onChange={(e) => setEditingLink((p) => ({ ...p, value: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') saveEditLink();
          if (e.key === 'Escape') setEditingLink(null);
        }}
        autoFocus
        placeholder="https://..."
        style={{
          flex: 1, minWidth: 0, padding: '4px 8px', borderRadius: '6px',
          background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-medium)',
          color: 'var(--dp-text-primary)', fontSize: '11px', fontFamily: 'var(--dp-font)',
          outline: 'none',
        }}
      />
      <button
        type="button"
        onClick={saveEditLink}
        disabled={savingLink || !(editingLink.value || '').trim().startsWith('https://')}
        title={t('dashboard.community.link_save')}
        style={{
          width: '22px', height: '22px', borderRadius: '6px', border: 'none', padding: 0,
          background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)', color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', opacity: savingLink ? 0.6 : 1, flexShrink: 0,
        }}
      >
        <Check size={12} />
      </button>
      <button
        type="button"
        onClick={() => setEditingLink(null)}
        title={t('common.cancel')}
        style={{
          width: '22px', height: '22px', borderRadius: '6px', padding: 0,
          border: '1px solid var(--dp-border-medium)', background: 'transparent',
          color: 'var(--dp-text-tertiary)', display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
        }}
      >
        <X size={12} />
      </button>
    </div>
  );

  const renderEditPencil = (key) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditingLink({ key, value: platformSettings[key] || '' });
      }}
      title={t('dashboard.community.link_edit')}
      style={{
        width: '22px', height: '22px', borderRadius: '6px', padding: 0,
        border: 'none', background: 'transparent', color: 'var(--dp-text-tertiary)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0, transition: 'color 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = '#0DAEFF'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
    >
      <Pencil size={12} />
    </button>
  );
  const [stats, setStats] = useState({
    totalCanais: 0,
    totalVideos: 0,
    videosPostados: 0,
    videosProducao: 0
  });
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canaisRecentes, setCanaisRecentes] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userName, setUserName] = useState('');
  const [dashboardMode, setDashboardMode] = useState('LOADING'); // 'FULL', 'SAFE', 'ERROR'
  const [channelModal, setChannelModal] = useState({ open: false, action: null }); // For quick action channel selection
  const [pipelineFilter, setPipelineFilter] = useState(null); // Filter pipeline videos by status
  const [pipelineAllVideos, setPipelineAllVideos] = useState(null); // All user videos for pipeline filtering
  const [loadingPipelineVideos, setLoadingPipelineVideos] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const fetchAttemptedRef = React.useRef(false);

  // Versão dinâmica do app desktop (puxa de releases.darkplanner.com.br/latest.yml)
  const winInfo = useWindowsReleaseInfo();
  const showVersionBadge = !winInfo.loading && !winInfo.error && winInfo.version && winInfo.version !== '0.3.0';

  // Contagem dinâmica de vozes premium (puxa de /api/audio-tts/voices)
  const [voicesCount, setVoicesCount] = useState(null);
  useEffect(() => {
    const fetchVoicesCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/audio-tts/voices`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) return;
        const data = await response.json();
        const total = data.total ?? data.voices?.length;
        if (total) setVoicesCount(total);
      } catch (err) {
        console.warn('[Dashboard] Falha ao buscar contagem de vozes:', err);
      }
    };
    fetchVoicesCount();
  }, []);



  const localeMap = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };

  // Formata a data atual por extenso no idioma selecionado
  const getFormattedDate = () => {
    const locale = localeMap[i18n.language] || 'pt-BR';
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    const date = new Date().toLocaleDateString(locale, options);
    // Capitalize first letter
    return date.charAt(0).toUpperCase() + date.slice(1);
  };

  useEffect(() => {
    // Evitar dupla execucao no StrictMode
    if (fetchAttemptedRef.current) return;
    fetchAttemptedRef.current = true;

    fetchStats().then(() => fetchAllVideos());
    checkIfAdmin();
  }, []);


  const checkIfAdmin = () => {
    axios.get(`${API}/auth/me`)
      .then((response) => {
        setIsAdmin(response.data.is_super_admin || false);
        setIsSuperAdmin(response.data.is_super_admin || false);
        const fullName = response.data.full_name || (response.data.username?.startsWith('USER_') ? null : response.data.username) || response.data.email?.split('@')[0] || 'Usuario';
        const firstName = fullName.split(' ')[0];
        setUserName(firstName);
      })
      .catch(() => {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setUserName('Usuario');
      });
  };

  // Funcao helper para fetch com timeout
  const fetchWithTimeout = async (url, timeoutMs = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const fetchStats = async () => {
    try {
      // Buscar dados basicos (sempre funciona)
      const summaryRes = await axios.get(`${API}/dashboard/summary`).catch(() => ({
        data: { totalCanais: 0, totalVideos: 0, videosPostados: 0, videosProducao: 0 }
      }));

      setStats(summaryRes.data);

      const listasRes = await axios.get(`${API}/dashboard/listas?limit=3`).catch(() => ({
        data: { canaisRecentes: [] }
      }));

      setCanaisRecentes(listasRes.data.canaisRecentes || []);

      // Tentar buscar stats completos com timeout de 8 segundos
      try {
        const statsResponse = await fetchWithTimeout(`${API}/dashboard/stats`, 8000);

        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setDashboardStats(statsData);
          setDashboardMode('FULL');
          setLoading(false);
          return;
        }
      } catch (error) {
        console.warn('[FALLBACK] Stats endpoint timeout ou falhou, usando modo seguro:', error.message);
      }

      // Fallback: Modo seguro com dados basicos
      setDashboardMode('SAFE');
      setLoading(false);

    } catch (error) {
      console.error('[ERROR] Falha ao carregar dashboard:', error);
      setDashboardMode('ERROR');
      setLoading(false);
    }
  };

  // ===== STAT CARD CONFIGS =====
  const statCardsConfig = [
    {
      label: t('dashboard.total_channels'),
      icon: <Youtube size={16} />,
      emoji: '📺',
      color: 'var(--dp-blue-500)',
      bgColor: 'rgba(13, 174, 255, 0.12)',
      getValue: () => dashboardStats?.visao_geral?.total_canais ?? stats.totalCanais,
      safeValue: () => stats.totalCanais,
      getTrend: () => {
        if (!dashboardStats) return null;
        const canaisData = dashboardStats.alertas_por_canal || [];
        // Count channels — this is a "positive" stat, just show total
        const count = canaisData.length;
        if (count > 0) return { type: 'positive', text: t('dashboard.trend_new_this_month', { count }) };
        return null;
      },
    },
    {
      label: t('dashboard.in_production'),
      icon: <Clapperboard size={16} />,
      emoji: '🎬',
      color: 'var(--dp-yellow-500)',
      bgColor: 'rgba(251, 191, 36, 0.12)',
      getValue: () => dashboardStats?.atividade?.producao ?? stats.videosProducao,
      safeValue: () => stats.videosProducao,
      getTrend: () => {
        if (!dashboardStats) return null;
        // Check if any channel has overdue videos (atrasados > 0) as a warning
        const totalAtrasados = (dashboardStats.alertas_por_canal || []).reduce((sum, c) => sum + (c.atrasados || 0), 0);
        if (totalAtrasados > 0) return { type: 'warning', text: t('dashboard.trend_near_deadline', { count: totalAtrasados }) };
        const emProd = dashboardStats.atividade?.producao || 0;
        if (emProd > 0) return { type: 'positive', text: t('dashboard.trend_all_on_time') };
        return null;
      },
    },
    {
      label: t('dashboard.published'),
      icon: <CheckCircle2 size={16} />,
      emoji: '✅',
      color: 'var(--dp-green-500)',
      bgColor: 'rgba(52, 211, 153, 0.12)',
      getValue: () => dashboardStats?.visao_geral?.total_videos_publicados ?? stats.videosPostados,
      safeValue: () => stats.videosPostados,
      getTrend: () => {
        if (!dashboardStats) return null;
        // Count recently published from alertas_por_canal
        const totalPublicados = (dashboardStats.alertas_por_canal || []).reduce((sum, c) => sum + (c.postado || 0), 0);
        if (totalPublicados > 0) return { type: 'positive', text: t('dashboard.trend_this_week', { count: totalPublicados }) };
        return null;
      },
    },
    {
      label: t('dashboard.scheduled'),
      icon: <CalendarCheck size={16} />,
      emoji: '📅',
      color: 'var(--dp-purple-500)',
      bgColor: 'rgba(167, 139, 250, 0.10)',
      getValue: () => dashboardStats?.atividade?.agendados ?? 0,
      safeValue: () => 0,
      getTrend: () => {
        if (!dashboardStats) return null;
        const totalAtrasados = (dashboardStats.alertas_por_canal || []).reduce((sum, c) => sum + (c.atrasados || 0), 0);
        if (totalAtrasados > 0) return { type: 'negative', text: t('dashboard.trend_overdue', { count: totalAtrasados }) };
        const totalAgendados = dashboardStats.atividade?.agendados || 0;
        if (totalAgendados > 0) return { type: 'positive', text: t('dashboard.trend_all_on_time') };
        return null;
      },
    }
  ];

  // Barra superior PADRÃO (= colunas do Text to Speech) em todos os stat cards.
  const barGradients = [
    'linear-gradient(90deg, #0DAEFF, #3B82F6)',
    'linear-gradient(90deg, #0DAEFF, #3B82F6)',
    'linear-gradient(90deg, #0DAEFF, #3B82F6)',
    'linear-gradient(90deg, #0DAEFF, #3B82F6)',
  ];

  // ===== QUICK ACTIONS CONFIG =====
  // Actions that need channel selection first
  const handleQuickAction = (action) => {
    if (action.needsChannel) {
      const canais = dashboardStats?.alertas_por_canal || [];
      if (canais.length === 0) {
        // No channels — go to channel creation
        navigate('/canais');
        return;
      }
      if (canais.length === 1) {
        // Only one channel — go directly
        localStorage.setItem('selectedCanal', canais[0].canal_id);
        navigate(action.path);
        return;
      }
      // Multiple channels — show modal
      setChannelModal({ open: true, action });
    } else {
      navigate(action.path);
    }
  };

  const audioSubtitle = voicesCount
    ? t('dashboard.quick_actions.generate_audio_subtitle', { count: voicesCount })
    : t('dashboard.quick_actions.generate_audio_subtitle_fallback');

  const quickActions = [
    {
      label: t('dashboard.generate_audio'),
      subtitle: audioSubtitle,
      icon: Mic,
      path: '/audio-generator',
      color: 'var(--dp-blue-500)',
      iconBg: 'rgba(13, 174, 255, 0.10)',
      hoverBorderColor: 'rgba(13, 174, 255, 0.30)',
      hoverShadow: '0 0 16px rgba(13, 174, 255, 0.08)',
      needsChannel: false,
    },
    {
      label: t('dashboard.quick_actions.viral_niches'),
      subtitle: t('dashboard.quick_actions.viral_niches_subtitle'),
      icon: Flame,
      path: '/nichos-virais',
      color: 'var(--dp-yellow-500)',
      iconBg: 'rgba(251, 191, 36, 0.10)',
      hoverBorderColor: 'rgba(251, 191, 36, 0.30)',
      hoverShadow: '0 0 16px rgba(251, 191, 36, 0.08)',
      needsChannel: false,
    },
    {
      label: t('dashboard.quick_actions.viral_videos'),
      subtitle: t('dashboard.quick_actions.viral_videos_subtitle'),
      icon: TrendingUp,
      path: '/videos-virais',
      color: 'var(--dp-green-500)',
      iconBg: 'rgba(52, 211, 153, 0.10)',
      hoverBorderColor: 'rgba(52, 211, 153, 0.30)',
      hoverShadow: '0 0 16px rgba(52, 211, 153, 0.08)',
      needsChannel: false,
    },
    {
      label: t('dashboard.quick_actions.channels_management'),
      subtitle: t('dashboard.quick_actions.channels_management_subtitle'),
      icon: Youtube,
      path: '/canais',
      color: 'var(--dp-purple-500)',
      iconBg: 'rgba(167, 139, 250, 0.10)',
      hoverBorderColor: 'rgba(167, 139, 250, 0.30)',
      hoverShadow: '0 0 16px rgba(167, 139, 250, 0.08)',
      needsChannel: false,
    },
  ];

  // ===== PIPELINE STAGES CONFIG (mesmas cores e labels do CanalManager) =====
  const pipelineStages = [
    { label: t('channels.planning'), color: 'var(--dp-blue-500, #0DAEFF)', key: 'planejamento' },
    { label: t('channels.production'), color: 'var(--dp-orange-500, #FB923C)', key: 'producao' },
    { label: t('channels.finished'), color: 'var(--dp-green-500, #34D399)', key: 'finalizado' },
    { label: t('channels.scheduled_published'), color: 'var(--dp-purple-500, #A78BFA)', key: 'postado' },
  ];

  // Usa video.status do backend diretamente (mesma fonte de verdade do CanalManager)
  const calcVideoStatus = (v) => v.status || 'planejamento';

  // Fetch all videos for pipeline filtering
  const fetchAllVideos = async () => {
    if (pipelineAllVideos) return pipelineAllVideos; // cached
    setLoadingPipelineVideos(true);
    try {
      const [videosRes, canaisRes] = await Promise.all([
        axios.get(`${API}/videos`),
        axios.get(`${API}/canais`),
      ]);
      const canaisMap = {};
      (canaisRes.data || []).forEach(c => { canaisMap[c.id] = c.nome; });
      const allVids = (videosRes.data || []).map(v => ({
        titulo: v.titulo || `Vídeo #${v.numero_video || '?'}`,
        canal_nome: canaisMap[v.canal_id] || '',
        status: calcVideoStatus(v),
        data_postagem: v.data_postagem,
        thumbnail: v.thumbnail || '',
        thumbnail_url: v.thumbnail_url || '',
        youtube_thumbnail: v.youtube_thumbnail || '',
        is_imported: v.is_imported || false,
        canal_id: v.canal_id,
        video_id: v.id,
        checklist_titulo: v.checklist_titulo || '',
        checklist_roteiro: v.checklist_roteiro || '',
        checklist_audio: v.checklist_audio || '',
        checklist_thumbnail: v.checklist_thumbnail || '',
        checklist_pronto: v.checklist_pronto || '',
        checklist_postagem: v.checklist_postagem || '',
      }));
      setPipelineAllVideos(allVids);
      return allVids;
    } catch {
      return [];
    } finally {
      setLoadingPipelineVideos(false);
    }
  };

  // Toggle pipeline filter — fetch all videos on first click
  const handlePipelineClick = async (stageKey) => {
    const newFilter = pipelineFilter === stageKey ? null : stageKey;
    setPipelineFilter(newFilter);
    if (newFilter && !pipelineAllVideos) {
      await fetchAllVideos();
    }
  };

  // Gather all videos for calendar from dashboard stats
  const getCalendarVideos = () => {
    if (!dashboardStats) return [];
    const videos = [];

    // From linha_tempo (today, tomorrow, day after, upcoming)
    const timeline = dashboardStats.linha_tempo || {};
    ['hoje', 'amanha', 'depois_amanha', 'proximos'].forEach(key => {
      (timeline[key] || []).forEach(v => {
        videos.push({
          titulo: v.titulo,
          canal_nome: v.canal,
          horario: v.horario,
          data_agendamento: v.data_completa || v.data,
          status: v.status || 'postado'
        });
      });
    });

    // From videos_recentes
    (dashboardStats.videos_recentes || []).forEach(v => {
      if (!videos.find(existing => existing.titulo === v.titulo)) {
        videos.push({
          titulo: v.titulo,
          canal_nome: v.canal_nome,
          horario: v.horario,
          data_agendamento: v.data_agendamento,
          data_publicacao: v.data_publicacao,
          status: v.status
        });
      }
    });

    return videos;
  };

  // ===== LOADING STATE =====
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--dp-bg-primary)'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid var(--dp-border-subtle)',
          borderTop: '3px solid var(--dp-blue-500)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  // Helper: get pipeline value
  const getPipelineValue = (key) => {
    if (!dashboardStats) return 0;
    // Try visao_geral first, then atividade
    return dashboardStats.visao_geral?.[key] ?? dashboardStats.atividade?.[key] ?? 0;
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        fontFamily: 'var(--dp-font)',
        boxSizing: 'border-box',
      }}>

        {/* ====== 4.1 WELCOME SECTION ====== */}
        <div style={{ marginBottom: '28px' }}>
          <p style={{
            fontSize: '13px',
            color: 'var(--dp-text-tertiary)',
            fontWeight: '400',
            margin: '0 0 4px 0',
          }}>
            {getFormattedDate()}
          </p>
          <h1 style={{
            fontSize: '19px',
            fontWeight: '600',
            letterSpacing: '-0.3px',
            color: 'var(--dp-text-primary)',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            lineHeight: 1.2,
          }}>
            {t('dashboard.welcome')},{' '}
            <span style={{
              background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              {userName}
            </span>
            {' '}
            <span style={{ fontSize: '19px' }}>👋</span>
          </h1>
        </div>

        {/* ====== ERROR BANNER ====== */}
        {dashboardMode === 'ERROR' && (
          <div style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid rgba(248, 113, 113, 0.3)',
            borderRadius: 'var(--dp-radius-md)',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <AlertTriangle size={24} style={{ color: 'var(--dp-red-500)', margin: '0 auto 8px' }} />
            <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--dp-text-primary)', margin: '0 0 4px 0' }}>
              {t('dashboard.error_title')}
            </p>
            <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '0 0 12px 0' }}>
              {t('dashboard.error_desc')}
            </p>
            <button
              className="dp-btn-cta dp-btn-cta--sm"
              onClick={() => window.location.reload()}
            >
              {t('dashboard.reload')}
            </button>
          </div>
        )}

        {/* ====== SAFE MODE BANNER ====== */}
        {dashboardMode === 'SAFE' && (
          <div style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: 'var(--dp-radius-md)',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <AlertTriangle size={18} style={{ color: 'var(--dp-yellow-500)', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--dp-text-primary)', margin: '0 0 2px 0' }}>
                {t('dashboard.safe_mode')}
              </p>
              <p style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', margin: 0, lineHeight: '1.4' }}>
                {t('dashboard.safe_mode_desc')}
              </p>
            </div>
          </div>
        )}

        {/* ====== 4.2 STATS GRID (4 colunas) ====== */}
        <div className="cq-grid-4" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '14px',
          marginBottom: '24px',
          animation: 'dp-fade-in-up 0.4s ease',
        }}>
          {statCardsConfig.map((card, idx) => (
            <div
              key={card.label}
              style={{
                background: 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: 'var(--dp-radius-md)',
                padding: '18px 20px',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
                e.currentTarget.style.background = 'var(--dp-bg-card-hover)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                e.currentTarget.style.background = 'var(--dp-bg-card)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Colored top bar */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: barGradients[idx],
                opacity: 0.6,
                borderRadius: 'var(--dp-radius-md) var(--dp-radius-md) 0 0',
              }} />

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}>
                <span style={{
                  fontSize: '12px',
                  color: 'var(--dp-text-secondary)',
                  fontWeight: '500',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  {card.label}
                </span>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: card.bgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: card.color,
                }}>
                  {card.icon}
                </div>
              </div>

              <p style={{
                fontSize: '32px',
                fontWeight: '800',
                letterSpacing: '-1px',
                lineHeight: 1,
                margin: '0 0 4px 0',
                color: 'var(--dp-text-primary)',
              }}>
                {isPrivate ? '•' : (dashboardMode === 'FULL' ? card.getValue() : card.safeValue())}
              </p>

              {/* Trend badge */}
              {dashboardMode === 'FULL' && !isPrivate && card.getTrend && (() => {
                const trend = card.getTrend();
                if (!trend) return null;
                const trendStyles = {
                  positive: { background: 'rgba(16,185,129,0.15)', color: '#10B981' },
                  warning: { background: 'rgba(245,158,11,0.15)', color: '#F59E0B' },
                  negative: { background: 'rgba(239,68,68,0.15)', color: '#EF4444' },
                };
                const style = trendStyles[trend.type] || trendStyles.positive;
                const arrow = trend.type === 'negative' ? '\u25BC' : trend.type === 'warning' ? '\u26A0' : '\u25B2';
                return (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '3px 10px',
                    borderRadius: '20px',
                    marginTop: '8px',
                    fontFamily: 'var(--dp-font)',
                    ...style,
                  }}>
                    {arrow} {trend.text}
                  </span>
                );
              })()}
            </div>
          ))}
        </div>

        {/* ====== 4.3 QUICK ACTIONS (4 colunas) ====== */}
        <div className="cq-grid-actions cq-grid-4" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px',
          marginBottom: '24px',
          animation: 'dp-fade-in-up 0.4s ease',
          animationDelay: '0.1s',
          animationFillMode: 'backwards',
        }}>
          {quickActions.map((action) => {
            const IconComp = action.icon;
            return (
              <div
                key={action.label}
                onClick={() => handleQuickAction(action)}
                style={{
                  background: 'var(--dp-bg-card)',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '14px',
                  padding: '18px 20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = action.hoverBorderColor;
                  e.currentTarget.style.boxShadow = action.hoverShadow;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: action.iconBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: action.color,
                  flexShrink: 0,
                }}>
                  <IconComp size={18} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                  <span style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: 'var(--dp-text-primary)',
                    fontFamily: 'var(--dp-font)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {action.label}
                  </span>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'var(--dp-text-tertiary)',
                    fontFamily: 'var(--dp-font)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {action.subtitle}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ====== 4.4 NOVIDADES + DOWNLOADS/COMUNIDADE (2 colunas — Novidades em destaque) ====== */}
        {/* Grid com align-items:stretch (default). A coluna esquerda usa
            o truque "position:absolute inset:0" pra travar a altura igual
            à direita: conteúdo absolute sai do fluxo → wrapper intrinsic=0
            → row sizing pega apenas o intrinsic da direita → ambas as
            colunas (stretch) ficam exatamente da altura da direita. */}
        <div className="cq-grid-2" style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '20px',
          alignItems: 'stretch',
          marginBottom: '24px',
          animation: 'dp-fade-in-up 0.4s ease',
          animationDelay: '0.15s',
          animationFillMode: 'backwards',
        }}>
          {/* ---- Novidades da Plataforma (1ª coluna, mais larga) ---- */}
          {/* Wrapper relative com filho absolute inset:0 — sai do fluxo. */}
          <div className="dp-novidades-abs-wrapper" style={{ position: 'relative', minHeight: 0 }}>
            <div style={{ position: 'absolute', inset: 0 }}>
              <PlatformUpdatesPanel />
            </div>
          </div>

          {/* ---- Coluna direita: Downloads + Comunidade (empilhados) ---- */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* ---- Downloads (lista horizontal compacta) ---- */}
          {/* Card padding 0 + overflow hidden pra que a border-bottom do header
              estenda full-width e fique na mesma altura da border do Novidades. */}
          <div className="dp-top-accent" style={{
            background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
            borderRadius: '12px', padding: 0, fontFamily: 'var(--dp-font)',
            overflow: 'hidden',
          }}>
            {/* Header — padding 16/20 + min-height 56 + box-sizing border-box
                idêntico ao header do Novidades. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '12px', padding: '16px 20px', minHeight: '56px',
              borderBottom: '1px solid var(--dp-border-subtle)',
              boxSizing: 'border-box',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>
                <Download size={16} style={{ color: '#0DAEFF' }} />
                Downloads
              </span>
              <button
                onClick={() => navigate('/darkplanner-app')}
                style={{
                  fontSize: '11px', color: '#0DAEFF', background: 'none', border: 'none',
                  cursor: 'pointer', fontFamily: 'var(--dp-font)', fontWeight: 500,
                  boxShadow: 'none', transform: 'none', transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                Ver todos →
              </button>
            </div>

            {/* Lista compacta — padding interno (substitui o padding do card outer) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px' }}>
              {/* Item 1 — App Desktop Windows */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '10px 12px', borderRadius: '8px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--dp-border-subtle)',
              }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'rgba(13,174,255,0.12)', border: '1px solid rgba(13,174,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Monitor size={16} style={{ color: '#0DAEFF' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>App Desktop</span>
                    <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--dp-text-tertiary)' }}>Windows · macOS</span>
                    <span style={{
                      fontSize: '9px', fontWeight: 700, color: 'var(--dp-blue-500, #0DAEFF)',
                      background: 'rgba(13,174,255,0.12)', border: '1px solid rgba(13,174,255,0.3)',
                      padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap',
                    }}>
                      {winInfo.loading ? '...' : `v${winInfo.version}`}
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
                    Automatize e sincronize a geração de vídeos e imagens do <span style={{ color: 'var(--dp-blue-500, #0DAEFF)', fontWeight: 600 }}>VEO</span>
                  </div>
                </div>
                <a
                  href={winInfo.downloadUrl}
                  download
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                    minWidth: '100px', padding: '6px 14px', borderRadius: '7px',
                    background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)',
                    color: '#fff', fontSize: '11px', fontWeight: 600,
                    textDecoration: 'none', cursor: 'pointer', flexShrink: 0,
                    fontFamily: 'var(--dp-font)', boxShadow: 'none', transform: 'none',
                    transition: 'opacity 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  <Download size={12} />
                  Baixar
                </a>
              </div>

              {/* Item 2 — Extensão Chrome */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '10px 12px', borderRadius: '8px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--dp-border-subtle)',
              }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'rgba(13,174,255,0.12)', border: '1px solid rgba(13,174,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Puzzle size={16} style={{ color: '#0DAEFF' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>Extensão Chrome</div>
                  <div style={{ fontSize: '11px', fontWeight: 400, color: '#9CA3AF', marginTop: '2px' }}>
                    Para automação no navegador
                  </div>
                </div>
                <a
                  href="https://chromewebstore.google.com/detail/darkplanner/dkpcmhcjlidhmmnileiojcflhpclhcdn"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                    minWidth: '100px', padding: '6px 14px', borderRadius: '7px',
                    background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                    color: '#fff', fontSize: '11px', fontWeight: 600,
                    textDecoration: 'none', cursor: 'pointer', flexShrink: 0,
                    fontFamily: 'var(--dp-font)', boxShadow: 'none', transform: 'none',
                    transition: 'opacity 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  <Download size={12} />
                  Instalar
                </a>
              </div>
            </div>
          </div>

          {/* ---- CARD COMUNIDADE (segundo item da coluna direita) ---- */}
          <div className="dp-top-accent" style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <Users size={18} style={{ color: 'var(--dp-blue-500)' }} />
              <span style={{
                fontSize: '13px', fontWeight: 600,
                color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)',
              }}>
                {t('dashboard.community.title')}
              </span>
            </div>

            {/* Subtítulo */}
            <div style={{
              fontSize: '11px', fontWeight: 400, color: '#9CA3AF',
              fontFamily: 'var(--dp-font)', marginTop: '4px',
            }}>
              {t('dashboard.community.headline')}
            </div>

            {/* Sub-cards (WhatsApp primeiro, Discord depois) */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginTop: '12px',
            }}>
              {/* WhatsApp — comunidade SÓ para plano pago ativo (free NÃO vê o
                  item). Leva para /comunidade, onde ficam os 3 grupos (os links
                  só chegam ao navegador de quem paga; edição fica lá). */}
              {isPremium && (
                <div
                  onClick={() => navigate('/comunidade')}
                  style={{
                    background: 'rgba(37, 211, 102, 0.08)',
                    borderLeft: '3px solid #25D366',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37, 211, 102, 0.14)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(37, 211, 102, 0.08)'}
                >
                  <FaWhatsapp size={22} color="#25D366" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '12px', fontWeight: 600,
                      color: 'var(--dp-text-primary)',
                      fontFamily: 'var(--dp-font)',
                    }}>
                      WhatsApp
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: 'var(--dp-text-tertiary)',
                      fontFamily: 'var(--dp-font)',
                      marginTop: '2px',
                    }}>
                      {t('dashboard.community.whatsapp_subtitle')}
                    </div>
                  </div>
                  <ArrowRight size={14} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
                </div>
              )}

              {/* Discord — aberto a todos; super admin edita o link inline */}
              <div
                onClick={editingLink?.key === 'community_discord_url'
                  ? undefined
                  : () => window.open(platformSettings.community_discord_url, '_blank', 'noopener,noreferrer')}
                style={{
                  background: 'rgba(88, 101, 242, 0.08)',
                  borderLeft: '3px solid #5865F2',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: editingLink?.key === 'community_discord_url' ? 'default' : 'pointer',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(88, 101, 242, 0.14)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(88, 101, 242, 0.08)'}
              >
                <FaDiscord size={22} color="#5865F2" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '12px', fontWeight: 600,
                    color: 'var(--dp-text-primary)',
                    fontFamily: 'var(--dp-font)',
                  }}>
                    Discord
                  </div>
                  {editingLink?.key === 'community_discord_url' ? renderLinkEditor() : (
                    <div style={{
                      fontSize: '11px',
                      color: 'var(--dp-text-tertiary)',
                      fontFamily: 'var(--dp-font)',
                      marginTop: '2px',
                    }}>
                      {t('dashboard.community.discord_subtitle')}
                    </div>
                  )}
                </div>
                {editingLink?.key !== 'community_discord_url' && (
                  <>
                    {isSuperAdmin && renderEditPencil('community_discord_url')}
                    <ExternalLink size={14} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
                  </>
                )}
              </div>
            </div>
          </div>

          </div>
        </div>

        {/* ====== 4.7 RESUMO DOS CANAIS ====== */}
        {/* Aparece SEMPRE quando o dashboard estiver em modo FULL — quando não
            há canais cadastrados, mostra empty state com botão pra /canais */}
        {dashboardStats && dashboardMode === 'FULL' && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--dp-text-primary)',
            }}>
              <Youtube size={18} style={{ color: 'var(--dp-blue-500)' }} />
              {t('dashboard.channelSummary')}
            </h3>
            {dashboardStats.alertas_por_canal.length === 0 ? (
              /* EMPTY STATE — sem canais cadastrados */
              <div style={{
                background: 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '12px',
                padding: '48px 24px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                textAlign: 'center', fontFamily: 'var(--dp-font)',
              }}>
                <div style={{
                  width: '72px', height: '72px', borderRadius: '20px',
                  background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-subtle)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '20px',
                }}>
                  <Tv2 size={32} style={{ color: 'var(--dp-text-tertiary)', opacity: 0.5 }} />
                </div>
                <h4 style={{
                  fontSize: '18px', fontWeight: 700, color: 'var(--dp-text-primary)',
                  margin: '0 0 8px 0', fontFamily: 'var(--dp-font)',
                }}>
                  {t('channels.no_channels_title') || 'Você ainda não tem canais cadastrados'}
                </h4>
                <p style={{
                  fontSize: '14px', color: 'var(--dp-text-tertiary)',
                  margin: '0 0 24px 0', maxWidth: '400px', lineHeight: 1.5,
                }}>
                  {t('channels.no_channels_desc') || 'Crie seu primeiro canal para começar a gerenciar seus vídeos do YouTube'}
                </p>
                <button
                  className="dp-btn-cta dp-btn-cta--sm"
                  onClick={() => navigate('/canais')}
                >
                  <Plus size={14} />
                  {t('channels.create_first_channel') || 'Criar Primeiro Canal'}
                  <ArrowRight size={14} />
                </button>
              </div>
            ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '14px',
            }}>
              {dashboardStats.alertas_por_canal.map((canal, canalIdx) => {
                const isLinked = canal.is_youtube_linked;

                // Avatar color gradient based on index
                const avatarColors = [
                  ['#60A5FA', '#2563EB'],
                  ['#F472B6', '#DB2777'],
                  ['#34D399', '#059669'],
                  ['#FBBF24', '#D97706'],
                  ['#A78BFA', '#7C3AED'],
                  ['#FB923C', '#EA580C'],
                  ['#2DD4BF', '#0D9488'],
                  ['#FF6B6B', '#EE5A24'],
                  ['#C084FC', '#9333EA'],
                  ['#4ADE80', '#16A34A'],
                ];
                const [avColor1, avColor2] = avatarColors[canalIdx % avatarColors.length];
                const canalInitial = (canal.canal_nome || '?')[0].toUpperCase();

                // Tempo desde última sync
                const getSyncAgo = () => {
                  if (!canal.last_sync_at) return null;
                  try {
                    const diff = Date.now() - new Date(canal.last_sync_at).getTime();
                    const mins = Math.floor(diff / 60000);
                    if (mins < 60) return `${mins}min`;
                    const hrs = Math.floor(mins / 60);
                    if (hrs < 24) return `${hrs}h`;
                    const days = Math.floor(hrs / 24);
                    return `${days}d`;
                  } catch { return null; }
                };
                const syncAgo = getSyncAgo();

                return (
                  <div
                    key={canal.canal_id}
                    style={{
                      background: 'var(--dp-bg-card)',
                      border: '1px solid var(--dp-border-subtle)',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      cursor: 'pointer',
                      transition: 'all 0.25s',
                    }}
                    onClick={() => {
                      localStorage.setItem('selectedCanal', canal.canal_id);
                      navigate('/canais');
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.3)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {/* Card content with avatar on left */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                      {/* Avatar */}
                      <div style={{ flexShrink: 0, paddingTop: '2px' }}>
                        {!isPrivate && canal.foto_url ? (
                          <img
                            src={canal.foto_url}
                            alt=""
                            style={{
                              width: '42px', height: '42px', borderRadius: '50%',
                              objectFit: 'cover',
                              border: `2px solid ${avColor1}40`,
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        {(!canal.foto_url || isPrivate) && (
                          <div style={{
                            width: '42px', height: '42px', borderRadius: '50%',
                            background: `linear-gradient(135deg, ${avColor1}, ${avColor2})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: `0 2px 8px ${avColor1}30`,
                          }}>
                            <span style={{
                              fontSize: '17px', fontWeight: 700, color: '#fff',
                              fontFamily: 'var(--dp-font)',
                              textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                            }}>
                              {isPrivate ? '•' : canalInitial}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Right side: name + stats */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Channel name + badge */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{
                            fontSize: '14px', fontWeight: 600, flex: 1,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                          }}>
                            {isPrivate ? '••••••••' : canal.canal_nome}
                          </span>
                          {/* Sync badge or not-linked badge */}
                          {isLinked && syncAgo ? (
                            <span style={{
                              fontSize: '10px', color: 'var(--dp-text-tertiary)',
                              display: 'flex', alignItems: 'center', gap: '3px',
                              flexShrink: 0, whiteSpace: 'nowrap',
                            }}>
                              {t('dashboard.updatedAgo')} {syncAgo}
                            </span>
                          ) : !isLinked ? (
                            <span style={{
                              fontSize: '10px', color: 'var(--dp-yellow-500)',
                              display: 'flex', alignItems: 'center', gap: '3px',
                              flexShrink: 0, whiteSpace: 'nowrap',
                            }}>
                              <AlertTriangle size={10} />
                              {t('dashboard.notLinked')}
                            </span>
                          ) : null}
                        </div>

                        {/* YouTube stats — só se vinculado */}
                    {isLinked ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
                        padding: '8px 0', marginBottom: '8px',
                        background: 'rgba(13, 174, 255, 0.04)',
                        borderRadius: '8px',
                        border: '1px solid rgba(13, 174, 255, 0.08)',
                      }}>
                        {/* Subscribers */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Users size={12} style={{ color: 'var(--dp-text-secondary)' }} />
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                              {isPrivate ? '•••' : formatNumber(canal.youtube_subscribers)}
                            </span>
                          </div>
                          <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            SUBS
                          </span>
                        </div>
                        {/* Views */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Eye size={12} style={{ color: 'var(--dp-text-secondary)' }} />
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                              {isPrivate ? '•••' : formatNumber(canal.youtube_total_views)}
                            </span>
                          </div>
                          <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            VIEWS
                          </span>
                        </div>
                        {/* Vídeos */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Video size={12} style={{ color: 'var(--dp-text-secondary)' }} />
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                              {isPrivate ? '•' : canal.youtube_video_count}
                            </span>
                          </div>
                          <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            VÍDEOS
                          </span>
                        </div>
                      </div>
                    ) : (
                      /* Não vinculado — banner */
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', marginBottom: '8px',
                        background: 'rgba(251, 191, 36, 0.06)',
                        borderRadius: '8px',
                        border: '1px solid rgba(251, 191, 36, 0.15)',
                      }}>
                        <span style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                          {t('dashboard.youtubeNotConnected')}
                        </span>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            localStorage.setItem('selectedCanal', canal.canal_id);
                            navigate('/canais');
                          }}
                          style={{
                            fontSize: '11px', fontWeight: 600,
                            color: 'var(--dp-blue-500)', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px',
                          }}
                        >
                          <Link2 size={12} />
                          {t('dashboard.linkChannel')}
                        </span>
                      </div>
                    )}
                      </div>
                    </div>

                    {/* DarkPlanner stats — linha compacta */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      fontSize: '11px', color: 'var(--dp-text-tertiary)',
                      fontFamily: 'var(--dp-font)', flexWrap: 'wrap',
                    }}>
                      <span>{t('dashboard.ch_production')}: <b style={{ color: 'var(--dp-text-secondary)' }}>{isPrivate ? '•' : canal.producao}</b></span>
                      <span style={{ opacity: 0.4 }}>·</span>
                      <span>{t('dashboard.ch_finished')}: <b style={{ color: 'var(--dp-text-secondary)' }}>{isPrivate ? '•' : canal.finalizado}</b></span>
                      <span style={{ opacity: 0.4 }}>·</span>
                      <span>{t('dashboard.ch_overdue')}: <b style={{ color: canal.atrasados > 0 ? '#F87171' : 'var(--dp-text-secondary)' }}>{isPrivate ? '•' : canal.atrasados}</b></span>
                      <span style={{ opacity: 0.4 }}>·</span>
                      <span>{t('dashboard.ch_published')}: <b style={{ color: 'var(--dp-text-secondary)' }}>{isPrivate ? '•' : canal.postado}</b></span>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}

        <CalendarModal
          isOpen={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          videos={getCalendarVideos()}
        />

      </main>

      {/* ====== CHANNEL SELECTION MODAL ====== */}
      {channelModal.open && (
        <div
          onClick={() => setChannelModal({ open: false, action: null })}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            animation: 'dp-fade-in 0.2s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--dp-bg-elevated)',
              border: '1px solid var(--dp-border-medium)',
              borderRadius: 'var(--dp-radius-lg, 16px)',
              padding: '24px',
              minWidth: '320px',
              maxWidth: '400px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
              animation: 'dp-fade-in-up 0.25s ease',
            }}
          >
            <h3 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--dp-text-primary)',
              margin: '0 0 16px 0',
              fontFamily: 'var(--dp-font)',
            }}>
              {t('dashboard.select_channel')}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {(dashboardStats?.alertas_por_canal || []).map((canal, idx) => {
                const colors = [
                  'var(--dp-blue-500)', 'var(--dp-green-500)', 'var(--dp-yellow-500)',
                  'var(--dp-red-500)', 'var(--dp-purple-500)', 'var(--dp-orange-500)',
                ];
                const dotColor = colors[idx % colors.length];
                return (
                  <div
                    key={canal.canal_id}
                    onClick={() => {
                      localStorage.setItem('selectedCanal', canal.canal_id);
                      navigate(channelModal.action.path);
                      setChannelModal({ open: false, action: null });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 14px',
                      borderRadius: 'var(--dp-radius-sm)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: '1px solid var(--dp-border-subtle)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--dp-blue-subtle)';
                      e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                    }}
                  >
                    <span style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: dotColor,
                      flexShrink: 0,
                    }} />
                    <span style={{
                      fontSize: '14px',
                      color: 'var(--dp-text-primary)',
                      fontFamily: 'var(--dp-font)',
                      flex: 1,
                    }}>
                      {isPrivate ? '••••••' : canal.canal_nome}
                    </span>
                    <ChevronRight size={16} style={{ color: 'var(--dp-text-tertiary)' }} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes dp-fade-in-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dp-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes dp-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .alerts-scroll::-webkit-scrollbar { width: 4px; }
        .alerts-scroll::-webkit-scrollbar-track { background: transparent; }
        .alerts-scroll::-webkit-scrollbar-thumb { background: rgba(13, 174, 255, 0.2); border-radius: 2px; }
        .dp-pipeline-scroll::-webkit-scrollbar { width: 3px; }
        .dp-pipeline-scroll::-webkit-scrollbar-track { background: transparent; }
        .dp-pipeline-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
        .dp-pipeline-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
        /* App Desktop card hover + arrow animation */
        .dp-app-card { transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease; }
        .dp-app-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(13, 174, 255, 0.25); border-color: var(--dp-blue-500); }
        .dp-app-arrow { transition: transform 0.2s ease; }
        .dp-app-card:hover .dp-app-arrow { transform: translateX(4px); }
        /* Container queries are now handled globally in index.css via
           .cq-grid-4, .cq-grid-2, .cq-grid-actions classes.
           They respond to the actual content width, not viewport. */
      `}</style>
    </div>
  );
};

// ===== SCHEDULE DAY COMPONENT =====
const ScheduleDay = ({ label, isToday, videos, isPrivate, borderColor, isLast, showDate, t }) => (
  <div style={{
    padding: '12px 0',
    borderBottom: isLast ? 'none' : '1px solid var(--dp-border-subtle)',
  }}>
    <div style={{
      fontSize: '12px',
      fontWeight: '600',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: 'var(--dp-text-primary)',
    }}>
      {label}
      {isToday && (
        <span style={{
          background: 'var(--dp-blue-500)',
          color: 'white',
          fontSize: '10px',
          padding: '2px 8px',
          borderRadius: '10px',
          fontWeight: '500',
        }}>
          {t('dashboard.today')}
        </span>
      )}
    </div>
    {videos.length > 0 ? (
      videos.map((video, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 12px',
            background: 'var(--dp-bg-input)',
            borderRadius: 'var(--dp-radius-sm)',
            marginBottom: '6px',
            cursor: 'pointer',
            transition: 'background 0.2s',
            borderLeft: `3px solid ${borderColor}`,
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--dp-bg-card-hover)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--dp-bg-input)'}
        >
          <span style={{
            fontSize: '12px',
            fontWeight: '600',
            color: 'var(--dp-text-secondary)',
            minWidth: showDate ? '80px' : '44px',
          }}>
            {showDate && video.data ? `${video.data.substring(0, 5)} ${video.horario || ''}`.trim() : (video.horario || '--:--')}
          </span>
          <span style={{
            fontSize: '13px',
            fontWeight: '400',
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--dp-text-primary)',
          }}>
            {isPrivate ? '••••••••••' : video.titulo}
          </span>
          <span style={{
            fontSize: '10px',
            color: 'var(--dp-text-tertiary)',
            background: 'var(--dp-bg-card)',
            padding: '2px 8px',
            borderRadius: '10px',
          }}>
            {isPrivate ? '••••' : video.canal}
          </span>
        </div>
      ))
    ) : (
      <p style={{
        fontSize: '13px',
        color: 'var(--dp-text-tertiary)',
        padding: '8px 0',
        fontStyle: 'italic',
        margin: 0,
      }}>
        {t('dashboard.no_schedules')}
      </p>
    )}
  </div>
);

export default DashboardNew;
